import React, { useState } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { getMaterialOptions } from "features/Materials/services/MaterialService";
import { getUserOptionsWithEmployeeProfile } from "features/Users/services/UserService";
import {
  EmployeeOption,
  EmployeeSingleValue,
} from "features/Employees/components/EmployeeSelectComponents";
import { getWorkOrderOptions } from "features/WorkOrders/services/WorkOrderService";
import {
  WorkOrderOption,
  WorkOrderSingleValue,
} from "features/WorkOrders/components/WorkOrderSelectComponents";
import { getPettyWorkOptions } from "features/PettyWorks/services/PettyWorkService";
import {
  PettyWorkOption,
  PettyWorkSingleValue,
} from "features/PettyWorks/components/PettyWorkSelectComponents";
import { getAmcOptions } from "features/Amc/services/AmcService";
import {
  AmcOption,
  AmcSingleValue,
} from "features/Amc/components/AmcSelectComponents";
import {
  createMaterialInOut,
  updateMaterialInOut,
} from "features/MaterialInOuts/services/MaterialInOutService";
import {
  MaterialInOutSourceType,
  MaterialInOutSourceTypeLabel,
} from "features/MaterialInOuts/constants/material-in-out.constants";
import { MaterialInOut } from "features/MaterialInOuts/types/material-in-out.types";
import schema from "./schema";

const SOURCE_TYPE_OPTIONS = [
  { value: MaterialInOutSourceType.WORK_ORDER, label: "Work Order" },
  { value: MaterialInOutSourceType.PETTY_WORK, label: "Petty Work" },
  { value: MaterialInOutSourceType.AMC, label: "AMC" },
  { value: MaterialInOutSourceType.OTHER, label: "Other" },
];

function fetchOptionsForSourceType(sourceType: string) {
  if (sourceType === MaterialInOutSourceType.WORK_ORDER)
    return getWorkOrderOptions;
  if (sourceType === MaterialInOutSourceType.PETTY_WORK)
    return getPettyWorkOptions;
  return getAmcOptions;
}

function selectComponentsForSourceType(sourceType: string) {
  if (sourceType === MaterialInOutSourceType.WORK_ORDER) {
    return { Option: WorkOrderOption, SingleValue: WorkOrderSingleValue };
  }
  if (sourceType === MaterialInOutSourceType.PETTY_WORK) {
    return { Option: PettyWorkOption, SingleValue: PettyWorkSingleValue };
  }
  return { Option: AmcOption, SingleValue: AmcSingleValue };
}

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * When null, create. When set, edit that row — reachable while
   * `dateIn == null`, or for any row when the actor is Super Admin
   * (post-return editing).
   */
  editingRecord: MaterialInOut | null;
};

export function MaterialInOutCreateModal({
  show,
  onClose,
  onSuccess,
  editingRecord,
}: Props) {
  const isEdit = editingRecord != null;
  const { user } = useProfile();
  /** Any role can check material out against any user — default to self for convenience only. */
  const selfUserId = user?.id ?? null;

  const [materialDefaultOption, setMaterialDefaultOption] = useState<
    { value: number; label: string } | undefined
  >(
    editingRecord
      ? { value: editingRecord.material.id, label: editingRecord.material.name }
      : undefined,
  );
  const [userDefaultOption, setUserDefaultOption] = useState<
    { value: number; label: string } | undefined
  >(() => {
    if (editingRecord) {
      return {
        value: editingRecord.user.id,
        label:
          [editingRecord.user.firstName, editingRecord.user.lastName]
            .filter(Boolean)
            .join(" ")
            .trim() || `User #${editingRecord.user.id}`,
      };
    }
    if (user?.id) {
      return {
        value: user.id,
        label:
          [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
          "You",
      };
    }
    return undefined;
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      materialId: editingRecord?.materialId ?? "",
      userId: editingRecord?.userId ?? (selfUserId ?? ""),
      sourceType: editingRecord?.sourceType ?? MaterialInOutSourceType.WORK_ORDER,
      sourceId: editingRecord?.sourceId ?? "",
      otherDescription: editingRecord?.otherDescription ?? "",
      quantity: editingRecord?.quantity ?? 1,
      dateOut: editingRecord?.dateOut ?? "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        if (isEdit && editingRecord) {
          const res = await updateMaterialInOut(editingRecord.id, {
            materialId: Number(values.materialId),
            userId: Number(values.userId),
            quantity: Number(values.quantity),
            dateOut: values.dateOut || undefined,
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          } else {
            toast.error(res.message ?? "Could not update record.");
          }
        } else {
          const isOther = values.sourceType === MaterialInOutSourceType.OTHER;
          const res = await createMaterialInOut({
            materialId: Number(values.materialId),
            userId: Number(values.userId),
            sourceType: values.sourceType as MaterialInOutSourceType,
            sourceId: isOther ? undefined : Number(values.sourceId),
            otherDescription: isOther
              ? values.otherDescription.trim()
              : undefined,
            quantity: Number(values.quantity),
            dateOut: values.dateOut || undefined,
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          } else {
            toast.error(res.message ?? "Could not create record.");
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            (isEdit ? "Failed to update record" : "Failed to create record"),
        );
      }
    },
  });

  const handleSourceTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    formik.setFieldValue("sourceType", value);
    formik.setFieldValue("sourceId", "");
    formik.setFieldValue("otherDescription", "");
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const isOtherSource =
    formik.values.sourceType === MaterialInOutSourceType.OTHER;

  return (
    <Modal isOpen={show} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit material check-out" : "Check out material"}
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          {!isEdit ? (
            <>
              <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                Source
              </div>
              <Row className="g-3 mb-2">
                <Col md={12}>
                  <Label className="form-label">Source type</Label>
                  <div className="d-flex gap-4 pt-1 flex-wrap">
                    {SOURCE_TYPE_OPTIONS.map((opt) => (
                      <div className="form-check" key={opt.value}>
                        <Input
                          type="radio"
                          className="form-check-input"
                          id={`mio-source-type-${opt.value}`}
                          name="sourceType"
                          value={opt.value}
                          checked={formik.values.sourceType === opt.value}
                          onChange={handleSourceTypeChange}
                          onBlur={formik.handleBlur}
                        />
                        <Label
                          className="form-check-label"
                          for={`mio-source-type-${opt.value}`}
                        >
                          {opt.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {formik.errors.sourceType &&
                    (formik.touched.sourceType || formik.submitCount > 0) && (
                      <FormFeedback type="invalid" className="d-block">
                        {String(formik.errors.sourceType)}
                      </FormFeedback>
                    )}

                  <div className="mt-3">
                    <Label className="form-label" for="mio-source-entity">
                      {isOtherSource
                        ? "Description"
                        : (MaterialInOutSourceTypeLabel[
                            formik.values.sourceType as MaterialInOutSourceType
                          ] ?? "Source")}
                    </Label>
                    {isOtherSource ? (
                      <>
                        <Input
                          id="mio-source-entity"
                          type="text"
                          className="form-control"
                          placeholder="Describe where this material is going…"
                          name="otherDescription"
                          value={formik.values.otherDescription}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={Boolean(
                            formik.errors.otherDescription &&
                              (formik.touched.otherDescription ||
                                formik.submitCount > 0),
                          )}
                        />
                        {formik.errors.otherDescription &&
                          (formik.touched.otherDescription ||
                            formik.submitCount > 0) && (
                            <FormFeedback type="invalid" className="d-block">
                              {String(formik.errors.otherDescription)}
                            </FormFeedback>
                          )}
                      </>
                    ) : (
                      <>
                        <AsyncPaginatedSelect
                          key={formik.values.sourceType}
                          fetchOptions={fetchOptionsForSourceType(
                            formik.values.sourceType,
                          )}
                          components={selectComponentsForSourceType(
                            formik.values.sourceType,
                          )}
                          placeholder="Search…"
                          value={formik.values.sourceId}
                          onChange={(val: any) =>
                            formik.setFieldValue("sourceId", val?.value ?? "")
                          }
                          onBlur={() => formik.setFieldTouched("sourceId", true)}
                          isInvalid={
                            (formik.touched.sourceId || formik.submitCount > 0) &&
                            !!formik.errors.sourceId
                          }
                        />
                        {formik.errors.sourceId &&
                          (formik.touched.sourceId || formik.submitCount > 0) && (
                            <FormFeedback type="invalid" className="d-block">
                              {String(formik.errors.sourceId)}
                            </FormFeedback>
                          )}
                      </>
                    )}
                  </div>
                </Col>
              </Row>
              <hr className="my-3 text-muted opacity-25" />
            </>
          ) : (
            <>
              <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                Source
              </div>
              <p className="text-muted small mb-3">
                The source of a check-out can't be changed after creation.
                This one is linked to{" "}
                <strong>
                  {MaterialInOutSourceTypeLabel[editingRecord.sourceType]}
                </strong>
                {editingRecord.workLabel ? <>: {editingRecord.workLabel}</> : null}.
              </p>
              <hr className="my-3 text-muted opacity-25" />
            </>
          )}

          <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
            Material &amp; person
          </div>
          <Row className="g-3 mb-2">
            <Col md={6}>
              <Label className="form-label">
                Material <span className="text-danger">*</span>
              </Label>
              <AsyncPaginatedSelect
                fetchOptions={getMaterialOptions}
                defaultOption={materialDefaultOption}
                placeholder="Search materials…"
                value={formik.values.materialId}
                onChange={(val: any) => {
                  formik.setFieldValue("materialId", val?.value ?? "");
                  setMaterialDefaultOption(
                    val ? { value: val.value, label: val.label } : undefined,
                  );
                }}
                onBlur={() => formik.setFieldTouched("materialId", true)}
                isInvalid={
                  (formik.touched.materialId || formik.submitCount > 0) &&
                  !!formik.errors.materialId
                }
              />
              {formik.errors.materialId &&
                (formik.touched.materialId || formik.submitCount > 0) && (
                  <FormFeedback type="invalid" className="d-block">
                    {String(formik.errors.materialId)}
                  </FormFeedback>
                )}
            </Col>
            <Col md={6}>
              <Label className="form-label">
                Concerned person <span className="text-danger">*</span>
              </Label>
              <AsyncPaginatedSelect
                fetchOptions={getUserOptionsWithEmployeeProfile}
                components={{
                  Option: EmployeeOption,
                  SingleValue: EmployeeSingleValue,
                }}
                defaultOption={userDefaultOption}
                placeholder="Search users…"
                value={formik.values.userId}
                onChange={(opt: any) => {
                  const userId =
                    opt && !Array.isArray(opt) && opt.value != null
                      ? Number(opt.value)
                      : undefined;
                  formik.setFieldValue("userId", userId ?? "");
                  setUserDefaultOption(
                    opt && !Array.isArray(opt) && userId != null
                      ? { value: userId, label: opt.label }
                      : undefined,
                  );
                }}
                onBlur={() => formik.setFieldTouched("userId", true)}
                isInvalid={
                  (formik.touched.userId || formik.submitCount > 0) &&
                  !!formik.errors.userId
                }
              />
              {formik.errors.userId &&
                (formik.touched.userId || formik.submitCount > 0) && (
                  <FormFeedback type="invalid" className="d-block">
                    {String(formik.errors.userId)}
                  </FormFeedback>
                )}
            </Col>
          </Row>

          <hr className="my-3 text-muted opacity-25" />

          <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
            Quantity &amp; date
          </div>
          <Row className="g-3 mb-2">
            <Col md={6}>
              <Label className="form-label" for="mio-quantity">
                Quantity <span className="text-danger">*</span>
              </Label>
              <Input
                id="mio-quantity"
                type="number"
                className="form-control"
                placeholder="0"
                name="quantity"
                step="0.01"
                value={formik.values.quantity}
                onChange={(e) => {
                  const raw = e.target.value;
                  formik.setFieldValue("quantity", raw === "" ? "" : Number(raw));
                }}
                onBlur={formik.handleBlur}
                invalid={Boolean(
                  formik.errors.quantity &&
                    (formik.touched.quantity || formik.submitCount > 0),
                )}
              />
              {formik.errors.quantity &&
                (formik.touched.quantity || formik.submitCount > 0) && (
                  <FormFeedback type="invalid" className="d-block">
                    {String(formik.errors.quantity)}
                  </FormFeedback>
                )}
            </Col>
            <Col md={6}>
              <Label className="form-label" for="mio-date-out">
                Date out <span className="text-muted fw-normal">(optional)</span>
              </Label>
              <Flatpickr
                id="mio-date-out"
                className="form-control"
                placeholder="Defaults to now"
                options={{ altInput: true, altFormat: "d M, Y", dateFormat: "Y-m-d" }}
                onChange={(dates: Date[]) =>
                  formik.setFieldValue("dateOut", dates?.[0] ? toYmd(dates[0]) : "")
                }
                value={
                  formik.values.dateOut
                    ? new Date(`${formik.values.dateOut}T12:00:00`)
                    : ""
                }
              />
              <small className="text-muted d-block mt-1">
                Leave blank to record the current date/time.
              </small>
            </Col>
          </Row>

          <div className="d-flex gap-2 justify-content-end mt-3">
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={formik.isSubmitting}>
              {formik.isSubmitting
                ? isEdit
                  ? "Saving…"
                  : "Checking out…"
                : isEdit
                  ? "Save changes"
                  : "Check out"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}
